import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DefaultWellTestFormData } from '../../types/wellTestTypes';
import { saveDefaultWellTest } from '../../services/dashboard/DefaultWellTestService';

interface AddDefaultWellTestState {
  formData: DefaultWellTestFormData;
  isSubmitting: boolean;
  submitSuccess: boolean;
  submitError: string | null;
}

const initialState: AddDefaultWellTestState = {
  formData: {
    id: 0,
    testDate: '',
    duration: '',
    oilRate: '',
    waterRate: '',
    gasRate: '',
    tubingPressure: '',
    casingPressure: '',
    runtime: '',
    strokeLength: '',
    fluidAbovePump: '',
    pumpsize: '',
    spm: '',
    flowlineTemp: '',
    casingTemp: '',
    waterSG: '',
    oilAPIGravity: '',
    co2: '',
    highPressureGasRate: '',
    casingGasRate: '',
    gasLiftGasRate: '',
    flowlinePressure: '',
    tubingTemp: '',
    pumpIntakePressure: '',
    pumpDischargePressure: '',
    pumpIntakeTemp: '',
    motorTemp: '',
    driveCurrent: '',
    driveSpeed: '',
    rodRPM: '',
    rodTorque: '',
    triplexDischargePressure: '',
    suctionPressure: '',
    linePressureToSetting: '',
    wellId: null,
  },
  isSubmitting: false,
  submitSuccess: false,
  submitError: null,
};

// Async thunk to save Default well test
export const saveDefaultWellTestAsync = createAsyncThunk(
  'addDefaultWellTest/saveDefaultWellTest',
  async (
    { formData, assetId, isEdit }: { formData: DefaultWellTestFormData; assetId: string; isEdit: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await saveDefaultWellTest(formData, assetId, isEdit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to save Default well test');
    }
  },
);

const addDefaultWellTestSlice = createSlice({
  name: 'addDefaultWellTest',
  initialState,
  reducers: {
    setDefaultWellTestFormData: (state, action: PayloadAction<Partial<DefaultWellTestFormData>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetDefaultState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveDefaultWellTestAsync.pending, (state) => {
        state.isSubmitting = true;
        state.submitSuccess = false;
        state.submitError = null;
      })
      .addCase(saveDefaultWellTestAsync.fulfilled, (state) => {
        state.isSubmitting = false;
        state.submitSuccess = true;
        state.submitError = null;
      })
      .addCase(saveDefaultWellTestAsync.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitSuccess = false;
        state.submitError = action.payload as string;
      });
  },
});

export const { setDefaultWellTestFormData, resetDefaultState } = addDefaultWellTestSlice.actions;

export default addDefaultWellTestSlice.reducer;
